import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BoostModal from '../../../../common/BoostModal';
import ListingCard from '../../../../ListingCard';
import { ListingCardListing } from '../../../../ListingCard/types';
import { apiRequest } from '../../../../utils/api';
import {
  AreaInterest,
  criteriaFromForm,
  displayAreasFromForm,
} from '../areaInterestApi';
import { areaInterestToCardListing, criteriaPriceLabel } from '../areaInterestCard';
import { CouldBuyFormState } from '../types';

// Same asset the Activity row detail's boost band uses, so the two prompts read
// as one voice.
const JAAKKO_AVATAR = `${process.env.PUBLIC_URL}/assets/jaakko_avatar.png`;

// SMS match notifications are wired (JAA-280: the weekly area-interest digest sends
// email + opt-in SMS). The toggle is shown again; notify_sms still defaults off, so
// SMS stays strictly opt-in and the costly channel only reaches buyers who tick it.
const SMS_NOTIFY_ENABLED = true;

interface SuccessStepProps {
  form: CouldBuyFormState;
  // Present only when this success card represents a persisted area interest.
  // Building-only interests live in the willingness collection and stay static.
  areaInterestId?: string;
  onOpenInterest?: (listing: ListingCardListing) => void;
  // Anchor building label, shown as the card location for a building-only interest.
  anchorLabel?: string;
  // Criteria-matched sellers already listing on the building (building branch
  // only; the area branch never sets it). Above zero the card names the count
  // and swaps the Done CTA for a jump to the Activity row detail, where each
  // listing offers its own chat — the post itself opens no conversation.
  matchedListingCount?: number;
  // Editing an existing interest reaches this card too, so the copy has to say
  // "updated" rather than announcing it as newly active.
  isEditing?: boolean;
  // Sends the boost request for the interest that was just posted. Omit to hide
  // the boost promo, e.g. when the post returned no id to attach it to.
  onBoostConfirm?: () => Promise<void>;
  // Persists a notify-channel choice against the interest that was just created
  // or edited — an area interest's own document, or the digest notify flags on
  // a building-level willingness. Omit to hide the notify section entirely.
  onChange?: (updates: Partial<CouldBuyFormState>) => void;
  onDone: () => void;
  // Opens the Activity row detail of the just-posted building interest.
  onViewMatches?: () => void;
}

// Inline SVG for the notification bell used in the subtitle.
const BellIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 17 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: 'inline', verticalAlign: 'middle', marginLeft: 3 }}
    aria-hidden="true"
  >
    <path
      d="M8.5 2a5 5 0 0 1 5 5v3l1.5 2H2L3.5 10V7a5 5 0 0 1 5-5Z"
      stroke="#29363d"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
    <path d="M7 14.5a1.5 1.5 0 0 0 3 0" stroke="#29363d" strokeWidth="1.3" />
  </svg>
);

// Speech-bubble icon fronting the auto-match callout. Inline like the other
// glyphs in this file rather than an icon package, matching the flow's existing
// treatment. White because it sits on the solid green chip.
const ChatIcon = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M15.75 8.625c0 3.107-3.023 5.625-6.75 5.625-.83 0-1.626-.125-2.36-.354L2.25 15.75l1.2-3.026C2.7 11.575 2.25 10.15 2.25 8.625 2.25 5.518 5.273 3 9 3s6.75 2.518 6.75 5.625Z"
      stroke="#ffffff"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

// Checkmark icon used inside the checked checkbox.
const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M3.5 8.5l3.5 3.5 6.5-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface NotifyToggleProps {
  checked: boolean;
  label: string;
  onToggle: (next: boolean) => void;
}

// One notify row. The whole row is the control, so clicking the label text
// toggles it too (PR #116 N5): a button carrying role="checkbox" keeps Space and
// Enter working without nesting a second interactive element, which would fire
// the handler twice. The label keeps one colour in both states; only the box
// changes, so an unticked row no longer reads as a disabled feature.
const NotifyToggle = ({ checked, label, onToggle }: NotifyToggleProps) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    className="cbf-success__toggle-row"
    onClick={() => onToggle(!checked)}
  >
    <span
      className={`cbf-success__checkbox${checked ? ' cbf-success__checkbox--checked' : ''}`}
      aria-hidden="true"
    >
      {checked && <CheckIcon />}
    </span>
    <span
      className={`cbf-success__toggle-label${
        checked ? ' cbf-success__toggle-label--active' : ''
      }`}
    >
      {label}
    </span>
  </button>
);

// Step 3: post-submit confirmation. Shows the Figma summary card (coin tile + the
// primary location + criteria tokens, node 3670:59815), the "buying interest is now
// active" headline + subtitle, notify toggles, and Done/Share (or a jump to the
// matching listings in Activity when sellers already match). Matches Figma node
// 3670:55779.
const SuccessStep = ({
  form,
  areaInterestId,
  onOpenInterest,
  anchorLabel,
  matchedListingCount = 0,
  isEditing,
  onBoostConfirm,
  onChange,
  onDone,
  onViewMatches,
}: SuccessStepProps) => {
  const { t } = useTranslation();
  const [boostOpen, setBoostOpen] = useState(false);
  // True once we know the account has no verified phone. Ticking SMS is otherwise
  // a silent dead end: the digest drops the message with no feedback when the
  // number is missing or unverified (utils/notification_helper.py).
  const [smsUnreachable, setSmsUnreachable] = useState(false);

  // Save the SMS choice, then (only when opting in) check whether SMS can
  // actually reach this account and warn inline if it cannot. Checked here rather
  // than on mount so the request is paid for only by buyers who tick the box.
  const handleSmsToggle = async (next: boolean) => {
    onChange?.({ notifySms: next });
    if (!next) {
      setSmsUnreachable(false);
      return;
    }
    try {
      const settings = await apiRequest('/notification-settings');
      setSmsUnreachable(!settings?.phone_number || !settings?.phone_verified);
    } catch {
      setSmsUnreachable(false);
    }
  };

  // The just-posted interest in the persisted AreaInterest shape, so the card
  // helpers that My Listings uses can render it unchanged. Nothing here is
  // saved: the real record was created by the submit that led to this step.
  //
  // A building-only post selects no areas and no circle, so the area helpers
  // would render "No areas selected". The display-only `building` entry exists
  // for exactly this case: the anchor building IS the location.
  const displayAreas = displayAreasFromForm(form);
  const isBuildingOnly = displayAreas.length === 0 && form.anchorSelected;
  const previewInterest: AreaInterest = {
    area_interest_id: areaInterestId ?? 'preview',
    anchor_building_id: form.anchorSelected ? 'preview' : null,
    anchor_address: anchorLabel ? { street: anchorLabel } : null,
    display_areas:
      isBuildingOnly && anchorLabel
        ? [{ kind: 'building', name: anchorLabel }, ...displayAreas]
        : displayAreas,
    criteria: criteriaFromForm(form),
    notify_email: form.notifyEmail,
    notify_sms: form.notifySms,
    created_at: '',
  };

  return (
    <div className="cbf-step cbf-step--success">
      <div className="cbf-success__scroll">

        {/* The interest the user just posted, rendered through the same small
            buyer ListingCard as My Listings, so the preview and the saved item
            are the same object visually. */}
        <div className="cbf-success__preview">
          <ListingCard
            size="small"
            type="buyer"
            listing={
              areaInterestToCardListing(previewInterest, t) as unknown as ListingCardListing
            }
            priceText={criteriaPriceLabel(previewInterest) || undefined}
            priceTextShort={criteriaPriceLabel(previewInterest, true) || undefined}
            onOpen={areaInterestId ? onOpenInterest : undefined}
            isLoggedIn
          />
        </div>

        {/* Headline + subtitle */}
        <h2 className="cbf-success__headline">
          {t(`newLandingPage.couldBuy.success.${isEditing ? 'editHeadline' : 'headline'}`)}
        </h2>
        <p className="cbf-success__subtitle">
          {t(`newLandingPage.couldBuy.success.${isEditing ? 'editSubtitlePrefix' : 'subtitlePrefix'}`)}
          {' '}
          <strong className="cbf-success__subtitle-bold">
            {t('newLandingPage.couldBuy.success.subtitleActivity')}
          </strong>
          <BellIcon />
        </p>

        {/* Notify toggles. Both branches store them on their own document now:
            an area interest's record, or the digest notify flags on a
            building-level willingness. */}
        {onChange && (
          <div className="cbf-success__notify-section">
            <p className="cbf-success__notify-heading">
              {t('newLandingPage.couldBuy.success.notifyHeading')}
            </p>

            {/* SMS first (default off, strictly opt-in), gated by SMS_NOTIFY_ENABLED. */}
            {SMS_NOTIFY_ENABLED && (
              <NotifyToggle
                checked={form.notifySms}
                label={t('newLandingPage.couldBuy.success.notifySms')}
                onToggle={handleSmsToggle}
              />
            )}

            {smsUnreachable && (
              <p className="cbf-success__notify-hint">
                {t('newLandingPage.couldBuy.success.smsNeedsPhone')}
              </p>
            )}

            {/* Email second (default on) */}
            <NotifyToggle
              checked={form.notifyEmail}
              label={t('newLandingPage.couldBuy.success.notifyEmail')}
              onToggle={(next) => onChange({ notifyEmail: next })}
            />
          </div>
        )}

        {/* Boost prompt, in the Activity row detail's band treatment: Jaakko
            avatar, purple surface, inline link. Buyer-specific copy (JAA-329) —
            the Activity band keeps its own shared keys. */}
        {onBoostConfirm && (
          <div className="cbf-success__boost">
            <img
              className="cbf-success__boost-avatar"
              src={JAAKKO_AVATAR}
              alt=""
              aria-hidden="true"
            />
            <div className="cbf-success__boost-text">
              <p className="cbf-success__boost-title">
                {t('newLandingPage.couldBuy.boostPromo.title')}
              </p>
              <p className="cbf-success__boost-body">
                {t('newLandingPage.couldBuy.boostPromo.body')}
                {' '}
                <button
                  type="button"
                  className="cbf-success__boost-link"
                  onClick={() => setBoostOpen(true)}
                >
                  {t('newLandingPage.couldBuy.boostPromo.cta')}
                </button>
              </p>
            </div>
          </div>
        )}

        {/* Sits last so it reads directly into the "View matching listings" CTA
            it is talking about. Only a building-level post carries a count: the
            area branch is notify-only. The count is criteria-filtered — only
            sellers whose homes match what the buyer asked for — and no chat has
            been opened; the buyer starts one per listing from the Activity row. */}
        {matchedListingCount > 0 && onViewMatches && (
          <div className="cbf-success__callout">
            <span className="cbf-success__callout-icon" aria-hidden="true">
              <ChatIcon />
            </span>
            <p className="cbf-success__callout-text">
              {matchedListingCount === 1
                ? t('newLandingPage.couldBuy.success.matchedSellersOne')
                : t('newLandingPage.couldBuy.success.matchedSellersOther', {
                    count: matchedListingCount,
                  })}
            </p>
          </div>
        )}
      </div>

      {/* Done / View matching listings action */}
      <div className="cbf-success__footer">
        <button
          type="button"
          className="cbf-success__done-btn"
          onClick={matchedListingCount > 0 && onViewMatches ? onViewMatches : onDone}
        >
          {matchedListingCount > 0 && onViewMatches
            ? t('newLandingPage.couldBuy.success.viewMatches')
            : t('newLandingPage.couldBuy.success.done')}
        </button>
      </div>

      {onBoostConfirm && (
        <BoostModal
          isOpen={boostOpen}
          onClose={() => setBoostOpen(false)}
          onConfirm={onBoostConfirm}
          actionType="buy"
          buildingAddress={anchorLabel}
        />
      )}
    </div>
  );
};

export default SuccessStep;
